import React, {
  MutableRefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  TextInput as BaseTextInput,
  TextInputProps,
  View,
  NativeSyntheticEvent,
  TextInputFocusEventData,
  Platform,
  TextInputContentSizeChangeEventData,
} from 'react-native';
import { styled, SxProp } from 'dripsy';
import mergeRefs from 'react-merge-refs';

import { useColors } from '@hooks';
import { IS_MOBILE, IS_WEB, match } from '@utils';
import { Icon } from './Icon';
import { Touchable } from './Touchable';

const TEXT_INPUT_HEIGHT = 51;
// For multiline we will use padding vertical 19 + 16 + 16 = 51
const TEXT_INPUT_MIN_HEIGHT_MULTILINE = 19;

enum InnerIconPositionEnum {
  Right = 'right',
  Left = 'left',
}

type InnerIconType = string | { name: string; onPress(): void };

export type TextInputRefType = BaseTextInput | null;

export interface ITextInputProps extends TextInputProps {
  minHeight?: number;
  maxHeight?: number;
  iconLeft?: InnerIconType;
  iconRight?: InnerIconType;
  isError?: boolean;
  inputRef?: MutableRefObject<TextInputRefType> | null;
  hasClearButton?: boolean;
  disabled?: boolean;
}

interface IContainerStyleProps {
  isFocused: boolean;
  isError: boolean;
  multiline?: boolean;
  minHeight?: number;
  disabled?: boolean;
}

interface ITextInputStyleProps extends TextInputProps {
  height: number;
  minHeight?: number;
  maxHeight?: number;
  disabled?: boolean;
}

// TODO: find a way to fix ts error with dripsy
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const Input = styled(BaseTextInput)((props: ITextInputStyleProps) => {
  const textAlignVertical = props.multiline ? 'top' : 'center';

  const styles = Platform.select({
    web: { outlineStyle: 'none' },
    android: { textAlignVertical },
  });

  return {
    flex: Platform.select({ default: 1, web: null }),
    width: Platform.select({ default: null, web: '100%' }),
    minHeight: props.minHeight,
    maxHeight: props.maxHeight,
    height: props.height,
    pt: '$0',
    px: '$8',
    borderColor: '$background20',
    color: props.disabled ? '$text200' : '$heading800',
    ...styles,
  };
});

const Container = styled(View)((props: IContainerStyleProps) => ({
  minHeight: props.minHeight,
  height: props.multiline ? null : TEXT_INPUT_HEIGHT,
  py: props.multiline ? '$6' : null,
  width: '100%',
  flexDirection: 'row',
  borderWidth: 1,
  backgroundColor: '$background20',
  borderColor: match(
    '$background20',
    [props.isError, '$error300'],
    [props.isFocused, '$text400'],
  ),
  borderRadius: 12,
  alignItems: 'center',
}));

const baseIconContainerStyles: SxProp = {
  justifyContent: 'center',
  alignItems: 'center',
};

const IconLeftContainer = styled(Touchable)({
  ...baseIconContainerStyles,
  ml: '$5',
});

const IconRightContainer = styled(Touchable)({
  ...baseIconContainerStyles,
  mr: '$5',
});

const noop = () => {};

export function TextInput({
  onFocus,
  onBlur,
  onContentSizeChange,
  inputRef = null,
  secureTextEntry = false,
  value,
  isError = false,
  iconLeft,
  iconRight,
  hasClearButton,
  readOnly,
  disabled,
  maxHeight,
  ...props
}: ITextInputProps) {
  const colors = useColors();
  const innerInputRef = useRef<TextInputRefType>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isSecureTextEntry, setIsSecureTextEntry] =
    useState(secureTextEntry);

  const initHeight = props.multiline
    ? props.minHeight || TEXT_INPUT_MIN_HEIGHT_MULTILINE
    : TEXT_INPUT_HEIGHT;

  const [height, setHeight] = useState<number>(initHeight);

  useEffect(() => {
    resizeMultiline();
  }, [value]);

  function onFucusHandler(
    e: NativeSyntheticEvent<TextInputFocusEventData>,
  ) {
    // On WEB if even TextInput is not editable it anyway gets focus
    if (IS_WEB && isReadOnly) {
      return;
    }

    setIsFocused(true);
    onFocus?.(e);
  }

  function onBlurHandler(
    e: NativeSyntheticEvent<TextInputFocusEventData>,
  ) {
    setIsFocused(false);
    onBlur?.(e);
  }

  function onContentSizeChangeHandler(
    e: NativeSyntheticEvent<TextInputContentSizeChangeEventData>,
  ) {
    resizeMultiline(e);
    onContentSizeChange?.(e);
  }

  /**
   * We need to resize multiline TextInput manually
   * due to Textarea is not auto resizable
   * */
  function resizeMultiline(
    e?: NativeSyntheticEvent<TextInputContentSizeChangeEventData>,
  ) {
    if (!maxHeight || !props.multiline) {
      return;
    }

    const element = innerInputRef?.current as unknown as HTMLElement;

    if (IS_WEB && element?.scrollHeight) {
      // We set height = "auto" for component shrinking
      element.style.height = 'auto';
      element.style.height = `${Math.min(
        element.scrollHeight,
        maxHeight,
      )}px`;
    }

    if (IS_MOBILE && e?.nativeEvent) {
      const { contentSize } = e.nativeEvent;
      setHeight(Math.min(contentSize.height, maxHeight));
    }
  }

  function toggleIsSecureTextEntry() {
    setIsSecureTextEntry((v) => !v);
  }

  function clearText() {
    props.onChangeText?.('');
  }

  function renderInnerIcon(
    icon: InnerIconType,
    position: InnerIconPositionEnum,
  ) {
    const isString = typeof icon === 'string';
    const iconName = isString ? icon : icon.name;
    const iconOnPress = isString ? noop : icon.onPress;

    if (position === InnerIconPositionEnum.Left) {
      return (
        <IconLeftContainer onPress={iconOnPress}>
          <Icon name={iconName} color="$heading800" size={20} />
        </IconLeftContainer>
      );
    } else if (position === InnerIconPositionEnum.Right) {
      if (!!value && hasClearButton) {
        return (
          <IconRightContainer onPress={clearText}>
            <Icon name="close-light" color="$heading800" size={20} />
          </IconRightContainer>
        );
      } else if (iconRight) {
        return (
          <IconRightContainer onPress={iconOnPress}>
            <Icon name={iconName} color="$heading800" size={20} />
          </IconRightContainer>
        );
      }

      return null;
    }

    return null;
  }

  const isReadOnly =
    typeof readOnly !== 'undefined' ? readOnly : disabled;

  const toggleSecureTextEntryIcon = isSecureTextEntry
    ? 'show-light'
    : 'hide-light';

  return (
    <Container
      isFocused={isFocused}
      isError={isError}
      disabled={disabled}
      minHeight={props.minHeight}
      multiline={props.multiline}
    >
      {!!iconLeft &&
        renderInnerIcon(iconLeft, InnerIconPositionEnum.Left)}

      <Input
        ref={mergeRefs([innerInputRef, inputRef])}
        value={value}
        onFocus={onFucusHandler}
        onBlur={onBlurHandler}
        onContentSizeChange={onContentSizeChangeHandler}
        secureTextEntry={isSecureTextEntry}
        disabled={disabled}
        readOnly={isReadOnly}
        placeholderTextColor={colors.$text60}
        height={height}
        minHeight={props.minHeight}
        multiline={props.multiline}
        maxHeight={maxHeight}
        {...props}
      />

      {!!iconRight &&
        renderInnerIcon(iconRight, InnerIconPositionEnum.Right)}

      {!!value && secureTextEntry && (
        <IconRightContainer
          hitSlop={20}
          borderless
          onPress={toggleIsSecureTextEntry}
        >
          <Icon
            name={toggleSecureTextEntryIcon}
            color="$heading800"
            size={20}
          />
        </IconRightContainer>
      )}
      {!!isError && (
        <IconRightContainer disabled>
          <Icon
            name="info-circle-light"
            color="$error300"
            size={20}
          />
        </IconRightContainer>
      )}
    </Container>
  );
}
